// src/cognito/cognito.js
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js'
import { Config, CognitoIdentityCredentials } from 'aws-sdk'
import store from './../store'

export default class Cognito {
  configure (config) {
    if (config.userPool) {
      this.userPool = config.userPool
    } else {
      this.userPool = new CognitoUserPool({
        UserPoolId: config.UserPoolId,
        ClientId: config.ClientId
      })
    }
    // AWS SDK (v2) の設定（必要な場合のみ）
    if (config.region) {
      Config.region = config.region
    }
    if (config.IdentityPoolId) {
      Config.credentials = new CognitoIdentityCredentials({
        IdentityPoolId: config.IdentityPoolId
      })
    }
    this.options = config
    this.currentUser = false
  }

  // Vue.use(Cognito, options) で this.$cognito を提供
  static install = (Vue, options) => {
    Object.defineProperty(Vue.prototype, '$cognito', {
      get () { return this.$root._cognito }
    })
    Vue.mixin({
      beforeCreate () {
        if (this.$options.cognito) {
          this._cognito = this.$options.cognito
          this._cognito.configure(options)
        }
      }
    })
  }

  /**
   * サインアップ（username = email）
   */
  signUp (email, password, attrs = []) {
    const dataEmail = { Name: 'email', Value: email }
    const attributeList = [new CognitoUserAttribute(dataEmail)]

    if (attrs.length > 0) {
      for (const attr of attrs) {
        attributeList.push(new CognitoUserAttribute(attr))
      }
    }

    return new Promise((resolve, reject) => {
      this.userPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
  }

  /**
   * 確認コードで有効化
   */
  confirmation (username, confirmationCode) {
    const userData = { Username: username, Pool: this.userPool }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
  }

  /**
   * ログイン
   */
  login (username, password) {
    const userData = { Username: username, Pool: this.userPool }
    const cognitoUser = new CognitoUser(userData)
    const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password })
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          // 必要ならここで store に即時反映してもOK（既存フローが router.beforeResolve なら不要）
          // const idToken = result.getIdToken().getJwtToken()
          // store.commit('setUser', { nickname: username, token: idToken })
          this.currentUser = cognitoUser
          resolve(result)
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  /**
   * ログアウト
   */
  logout () {
    const cu = this.userPool.getCurrentUser()
    if (cu) cu.signOut()
    this.currentUser = false
  }

  /**
   * ログイン判定
   * 成功時: CognitoUserSession を resolve
   */
  isAuthenticated () {
    this.currentUser = this.userPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      if (this.currentUser === null) return reject(this.currentUser)
      this.currentUser.getSession((err, session) => {
        if (err) reject(err)
        else if (!session || !session.isValid()) reject(session)
        else resolve(session)
      })
    })
  }

  /**
   * 属性取得
   */
  getAttribute () {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) this.currentUser = this.userPool.getCurrentUser()
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.getUserAttributes((err, result) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
  }

  /**
   * 検証コード再送（メール）
   */
  resentCode () {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) this.currentUser = this.userPool.getCurrentUser()
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.getAttributeVerificationCode('email', {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err)
      })
    })
  }

  /**
   * Eメール検証
   */
  verifyAttribute (confirmationCode) {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) this.currentUser = this.userPool.getCurrentUser()
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.verifyAttribute('email', confirmationCode, {
        onSuccess: (result) => {
          const user = store.getters.user || {}
          user['email_verified'] = 'true'
          store.commit('setUser', user)
          resolve(result)
        },
        onFailure: (err) => reject(err)
      })
    })
  }

  /**
   * Eメール更新 → 未検証化
   */
  updateEmailAddress (email) {
    const attributes = { email, name: email }
    return new Promise((resolve, reject) => {
      this.updateAttributes(attributes)
        .then((result) => {
          const user = store.getters.user || {}
          user['email_verified'] = 'false'
          store.commit('setUser', user)
          resolve(result)
        })
        .catch(reject)
    })
  }

  /**
   * パスワード変更
   */
  updatePassword (oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) this.currentUser = this.userPool.getCurrentUser()
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
  }

  /**
   * パスワード忘れ：メール送信
   */
  forgetPassword (username) {
    const userData = { Username: username, Pool: this.userPool }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err)
      })
    })
  }

  /**
   * パスワードリセット（コード+新PW）
   */
  resetPassword (username, newPassword, code) {
    const userData = { Username: username, Pool: this.userPool }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err)
      })
    })
  }

  /**
   * 任意属性の更新
   */
  updateAttributes (attributes) {
    const attributeList = []
    for (const key in attributes) {
      attributeList.push(new CognitoUserAttribute({ Name: key, Value: attributes[key] }))
    }
    return new Promise((resolve, reject) => {
      if (!this.currentUser) this.currentUser = this.userPool.getCurrentUser()
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.updateAttributes(attributeList, (err, result) => {
        if (err) reject(err)
        else {
          const user = store.getters.user || {}
          for (const key in attributes) user[key] = attributes[key]
          store.commit('setUser', user)
          resolve(result)
        }
      })
    })
  }

  // ===== 便利メソッド（任意） =====

  /**
   * 現在のセッションを Promise で取得
   */
  getSession () {
    this.currentUser = this.userPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.getSession((err, session) => {
        if (err) reject(err)
        else resolve(session)
      })
    })
  }

  /**
   * ID トークン文字列を取得（未ログインなら null）
   */
  async getIdTokenString () {
    try {
      const session = await this.getSession()
      return session?.getIdToken()?.getJwtToken() || null
    } catch {
      return null
    }
  }

  /**
   * セッションのリフレッシュ（必要な場面で使用）
   */
  refreshSession () {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) this.currentUser = this.userPool.getCurrentUser()
      if (!this.currentUser) return reject(new Error('No current user'))
      this.currentUser.getSession((err, session) => {
        if (err || !session) return reject(err || new Error('No session'))
        const refreshToken = session.getRefreshToken()
        this.currentUser.refreshSession(refreshToken, (e, newSession) => {
          if (e) reject(e)
          else resolve(newSession)
        })
      })
    })
  }
}